/**
 * Owner billing worksheet → .xlsx.
 *
 * The accountant's job is to key our figures into their own system and issue the
 * invoice from there. This is the handover format: one flat row per booking, so they
 * can sort, filter and sum it, plus a header block naming the customer and the basis.
 *
 * ── Built from the SAME payload the page renders ─────────────────────────────
 * It takes the worksheet object, not its own query. That is the one structural rule
 * of this feature (tasks/internal-accounting-page.md §3): one builder, consumed by
 * the page and the export, so the file can never disagree with the screen somebody
 * approved. It works identically on a draft and on a finalised month because both
 * arrive in the same shape.
 *
 * ── Why this exists at all, given the history ────────────────────────────────
 * ⚠️ This does NOT reinstate the /finance dashboard exporter deleted in `869da211c`
 * (v1.1.53). That removal was deliberate and stands. This is a new, narrower export
 * for one page and one reader, and it is the reason `exceljs` is still a dependency.
 * Do not read it as a revert.
 *
 * ── Money and dates ──────────────────────────────────────────────────────────
 * Money is written as NUMERIC cells with a display format, never as pre-formatted
 * strings — a column of text does not sum, and the first thing an accountant does is
 * select a column and look at the status bar.
 *
 * Dates are written as ISO TEXT, deliberately. They are calendar days, already
 * resolved to the business timezone server-side; handing Excel a date object invites
 * it to reinterpret them against the workstation's locale and shift a checkout across
 * a month boundary. 'YYYY-MM-DD' sorts correctly as text and cannot be shifted.
 */

import {
  CUSTOMER_TYPE_LABELS,
  SOURCED_BY_LABELS,
  STANDARD_RATE_LABEL,
  ratePercent,
  billingWarningLabel,
  vatSourceLabel,
  FEE_VAT_LABEL,
  BILLING_FEE_BASIS_NOTE,
  PLATFORM_VAT_NOTE,
  REVENUE_FOOTING_NOTE,
  REVENUE_COLUMN_LABEL,
  VAT_COLUMN_LABEL,
  GROSS_INCL_VAT_COLUMN_LABEL,
  GROSS_INCL_VAT_NOTE,
  VAT_TREATMENT_COLUMN_LABEL,
  vatTreatmentLabel,
  PLATFORM_FACTS_GROUP_LABEL,
  HOST_ME_CALC_GROUP_LABEL,
  VAT_SOURCE_COLUMN_LABEL,
  PERIOD_START_COLUMN_LABEL,
  PERIOD_END_COLUMN_LABEL,
  INSTALMENT_COLUMN_LABEL,
  STAY_TOTAL_COLUMN_LABEL,
  STAY_BILLED_BEFORE_COLUMN_LABEL,
  STAY_REMAINING_COLUMN_LABEL,
  instalmentLabel,
  LONG_STAY_NOTE,
  BILLING_NOT_THE_REPORT_NOTE
} from '../config/ownerBillingLabels';

const MONEY_FMT = '#,##0.00';
/** The commission rate. Stored as a fraction, so Excel's own % format does the ×100. */
const RATE_FMT = '0.0%';

/** Column widths, in the order the header row below writes them. */
const COLUMNS = [
  { header: 'الوحدة', width: 30 },
  { header: 'كود الوحدة', width: 12 },
  { header: 'رقم الحجز', width: 20 },
  { header: 'المنصة', width: 14 },
  { header: 'الدخول', width: 13 },
  { header: 'المغادرة', width: 13 },
  // ── The period this LINE bills, which is the whole stay on an ordinary booking ──
  // Appended after the stay's own dates rather than replacing them: the accountant still
  // needs to see the booking he is looking at, and on 99% of rows the two pairs are the
  // same. ⚠️ «نهاية الفترة» is the departure morning — exclusive, not a billed night.
  { header: PERIOD_START_COLUMN_LABEL, width: 13 },
  { header: PERIOD_END_COLUMN_LABEL, width: 13 },
  { header: INSTALMENT_COLUMN_LABEL, width: 12 },
  // ── «بيانات المنصة» — checked against the platform's own statement (cols 10–13) ──
  // Headers imported, never retyped — the screen prints the same ones
  // (web/src/config/ownerBillingLabels.js). Group spans live in GROUPS below.
  { header: GROSS_INCL_VAT_COLUMN_LABEL, width: 20 },
  { header: VAT_COLUMN_LABEL, width: 20 },
  { header: VAT_SOURCE_COLUMN_LABEL, width: 14 },
  { header: 'عمولة المنصة', width: 14 },
  // ── «احتساب هوست مي» — checked against the contract (cols 14–19) ──
  { header: VAT_TREATMENT_COLUMN_LABEL, width: 14 },
  { header: REVENUE_COLUMN_LABEL, width: 18 },
  // The rate PER LINE. A month can mix the standard rate with an owner's negotiated
  // one, so a single percentage in the header cannot describe the sheet — the
  // accountant has to be able to read each figure without interpreting a footnote.
  { header: 'النسبة', width: 10 },
  { header: 'مصدر الحجز', width: 14 },
  { header: 'رسوم إدارة هوست مي', width: 20 },
  { header: 'محتسب', width: 10 },
  // Stay-level context on an instalment row, blank on every ordinary one — so a reader
  // filtering the sheet can reconcile a split stay without opening the other months.
  { header: STAY_TOTAL_COLUMN_LABEL, width: 16 },
  { header: STAY_BILLED_BEFORE_COLUMN_LABEL, width: 16 },
  { header: STAY_REMAINING_COLUMN_LABEL, width: 14 },
  { header: 'ملاحظات', width: 46 }
];

/** The two column groups, as 1-based inclusive spans over COLUMNS. */
const GROUPS = [
  { label: PLATFORM_FACTS_GROUP_LABEL, from: 10, to: 13 },
  { label: HOST_ME_CALC_GROUP_LABEL, from: 14, to: 19 }
];

/**
 * The group boundary on a row: a rule on the first cell of each group. `left` is the
 * LOGICAL side — the one adjacent to the previous column — and Excel's RTL view mirrors
 * the grid, so it lands between the groups on screen and on paper.
 */
const markBoundaries = (row) => {
  for (const g of GROUPS) {
    const cell = row.getCell(g.from);
    cell.border = { ...(cell.border || {}), left: { style: 'medium' } };
  }
};

/**
 * @param {Object} ExcelJS - the lazily-imported module (keeps ~1MB off first paint)
 * @param {Object} worksheet - the payload from GET /worksheet
 * @param {Object} [record] - the frozen record, when the month is final
 * @returns {Object} an ExcelJS workbook
 */
export function buildBillingWorkbook(ExcelJS, worksheet, record = null) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Host Me';

  const ws = workbook.addWorksheet('كشف رسوم الإدارة', { views: [{ rightToLeft: true }] });
  ws.columns = COLUMNS.map(c => ({ width: c.width }));

  const rtl = (cell) => {
    cell.alignment = { horizontal: 'right', readingOrder: 'rtl', vertical: 'middle' };
  };

  const addLabelled = (label, value) => {
    const row = ws.addRow([label, value]);
    rtl(row.getCell(1));
    rtl(row.getCell(2));
    row.getCell(1).font = { bold: true };
    return row;
  };

  // ── Header: who, when, and on what basis ──────────────────────────────────
  const title = ws.addRow(['كشف رسوم إدارة هوست مي']);
  title.getCell(1).font = { bold: true, size: 14 };
  rtl(title.getCell(1));

  addLabelled('العميل', worksheet.owner.billedName);
  addLabelled('نوع العميل', CUSTOMER_TYPE_LABELS[worksheet.owner.customerType] || 'غير محدد');
  addLabelled(
    'الرقم الضريبي',
    worksheet.owner.vatRegistered ? (worksheet.owner.vatNumber || 'غير مُدخل') : 'غير مسجّل'
  );
  addLabelled('شهر الفوترة', worksheet.periodMonth);
  // Draft or final is on the FILE, not only the screen — an exported draft that
  // circulates without saying so is exactly how a superseded figure gets invoiced.
  addLabelled('الحالة', record ? 'معتمد — الأرقام ثابتة' : 'مسودة — الأرقام قابلة للتغيّر');
  // ⚠️ Named as the STANDARD rate, not "the" rate: individual lines may differ, and
  // the per-line column below is where the truth for each figure lives.
  addLabelled(STANDARD_RATE_LABEL, ratePercent(worksheet.commissionRate));
  // The basis sentence is IMPORTED, not authored here — the screen states the same fact and
  // two spellings of one basis is drift invisible in a diff.
  addLabelled(REVENUE_COLUMN_LABEL, REVENUE_FOOTING_NOTE);
  addLabelled(GROSS_INCL_VAT_COLUMN_LABEL, GROSS_INCL_VAT_NOTE);
  addLabelled('أساس الاحتساب', BILLING_FEE_BASIS_NOTE);
  addLabelled('ضريبة الحجز', PLATFORM_VAT_NOTE);

  ws.addRow([]);

  // ── The rows ──────────────────────────────────────────────────────────────
  // The group tier first: one merged cell per group, above the headings it spans.
  const groupRow = ws.addRow(COLUMNS.map(() => ''));
  for (const g of GROUPS) {
    groupRow.getCell(g.from).value = g.label;
    ws.mergeCells(groupRow.number, g.from, groupRow.number, g.to);
    const cell = groupRow.getCell(g.from);
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', readingOrder: 'rtl', vertical: 'middle' };
    cell.border = { bottom: { style: 'medium' } };
  }
  markBoundaries(groupRow);
  const header = ws.addRow(COLUMNS.map(c => c.header));
  header.font = { bold: true };
  header.eachCell(rtl);
  markBoundaries(header);

  for (const group of worksheet.properties) {
    for (const row of group.rows) {
      const line = ws.addRow([
        group.name,
        group.code || '',
        row.reference,
        row.platform,
        row.checkIn || '',
        row.checkOut || '',
        row.periodStart || '',
        row.periodEnd || '',
        // Named only on a split stay. «قسط 1 من 1» on every ordinary row would bury the
        // handful that are actually instalments — the same reasoning as «من المالك» below.
        row.of > 1 ? instalmentLabel(row.sequence, row.of) : '',
        // Platform facts
        row.grossInclVat,
        row.vat,
        vatSourceLabel(row.vatSource, row.platform, row.vat),
        row.otaFee,
        // Host Me's calculation
        vatTreatmentLabel(row.revenueFooting, row.vatSource),
        row.revenue,
        // A NUMBER with a percent format, not the pre-formatted string — this file's rule
        // (see MONEY_FMT above) is that figures are numeric cells, so they sort and filter.
        row.commissionRate,
        // Only the exception is named. A column repeating «من هوست مي» on every ordinary
        // row hides the one row that differs, which is the column's entire purpose.
        row.sourcedBy === 'owner' ? SOURCED_BY_LABELS.owner : '',
        row.commission,
        row.billed ? 'نعم' : 'لا',
        row.stay ? row.stay.revenue : null,
        row.stay ? row.stay.billedBefore : null,
        row.stay ? row.stay.remaining : null,
        (row.warnings || []).map(w => billingWarningLabel(w.code)).join(' · ')
      ]);

      // Money: gross(10) · VAT(11) · OTA fee(13) · revenue(15) · our fee(18) · stay
      // total(20) · billed before(21) · remaining(22). Positional, because ExcelJS's
      // addRow(array) API has no column names — re-derive every index when COLUMNS moves.
      [10, 11, 13, 15, 18, 20, 21, 22].forEach(i => { line.getCell(i).numFmt = MONEY_FMT; });
      line.getCell(16).numFmt = RATE_FMT;
      [1, 2, 3, 4, 9, 12, 14, 17, 19, 23].forEach(i => rtl(line.getCell(i)));

      markBoundaries(line);

      // An unbilled row must survive the export as visibly unfinished. It carries no
      // figures at all — a blank cell is honest where 0.00 would claim we billed zero.
      if (!row.billed) line.font = { italic: true };
    }
  }

  ws.addRow([]);

  // ── Totals ────────────────────────────────────────────────────────────────
  // ⚠️ Label at 14, value at 15 — column 15 is «إيراد الوحدة», so the totals sit UNDER the
  // money they total (the fee is 25% of that column). A total floating over the wrong
  // column is the kind of thing an accountant reads as an error in the figures rather
  // than in the layout.
  const addTotal = (label, value, bold = false) => {
    const row = ws.addRow(['', '', '', '', '', '', '', '', '', '', '', '', '', label, value]);
    rtl(row.getCell(14));
    row.getCell(15).numFmt = MONEY_FMT;
    if (bold) { row.getCell(14).font = { bold: true }; row.getCell(15).font = { bold: true }; }
    return row;
  };

  // Same order as the screen: the platform's figures, then ours. ⚠️ `revenueVat` here is
  // the GUEST'S VAT on the room — the «ضريبة القيمة المضافة» line further down is the VAT
  // on OUR fee. Two taxes, two labels.
  addTotal(`إجمالي ${GROSS_INCL_VAT_COLUMN_LABEL}`, worksheet.totals.grossInclVat);
  addTotal(`إجمالي ${VAT_COLUMN_LABEL}`, worksheet.totals.revenueVat);
  addTotal(`إجمالي ${REVENUE_COLUMN_LABEL}`, worksheet.totals.revenue);
  // ⚠️ RATE-FREE. This used to print the statement's single rate, which is wrong the
  // moment a month mixes tiers — on the sheet the accountant bills from.
  addTotal('رسوم إدارة هوست مي', worksheet.totals.commission);
  if (record?.discount?.amount > 0) {
    addTotal(`خصم — ${record.discount.reason}`, -record.discount.amount);
  }
  addTotal(
    `${FEE_VAT_LABEL} (${Math.round(worksheet.vatRate * 100)}%)`,
    record ? record.billedTotals.vat : worksheet.totals.vat
  );
  addTotal('الإجمالي', record ? record.billedTotals.total : worksheet.totals.total, true);

  // ── The line that stops a question every month ────────────────────────────
  ws.addRow([]);
  // ⚠️ The CONSTANT, not a retyped copy. This sentence used to be spelled out here and
  // again in ownerBillingLabels.js, and the two drifted the moment instalments changed
  // what the page actually bills — drift invisible in a diff, which is the exact failure
  // that file exists to prevent.
  const note = ws.addRow([`ملاحظة: ${BILLING_NOT_THE_REPORT_NOTE}`]);
  rtl(note.getCell(1));
  note.getCell(1).font = { italic: true, size: 9 };

  // How a split stay was split — printed only when the sheet holds one, so an ordinary
  // month's export does not carry a paragraph about a rule it never applied.
  if (worksheet.properties.some(g => g.rows.some(r => r.of > 1))) {
    const longStay = ws.addRow([LONG_STAY_NOTE]);
    rtl(longStay.getCell(1));
    longStay.getCell(1).font = { italic: true, size: 9 };
  }

  return workbook;
}

/** `كشف رسوم الإدارة - محمد باقر - 2026-07.xlsx` — matches the owner-invoicing naming. */
export const billingWorkbookFilename = (worksheet) =>
  `كشف رسوم الإدارة - ${worksheet.owner.billedName} - ${worksheet.periodMonth}.xlsx`;
